import React, { useEffect, useState } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink } from 'react-router-dom';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import Logo from './logo.png';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';

const PHONE_LINK = '+393429118790';
const languages = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
];
const pages = [
  { target: 'Home', label: 'nav_home' },
  { target: 'About Us', label: 'nav_about' },
  { target: 'On-Demand', label: 'nav_services' },
  { target: 'Booking', label: 'nav_booking' },
  { target: 'Faq', label: 'nav_faq' },
  { target: 'Contact', label: 'nav_contact' },
];

const Navbar = ({ homeOnly = false }) => {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2).toUpperCase();

  useEffect(() => {
    const savedLanguage = Cookies.get('language');
    if (savedLanguage) i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    Cookies.set('language', code, { expires: 7 });
    setLanguageOpen(false);
  };

  const closeMenu = () => setMenuOpen(false);

  const Brand = () => (
    <span className="navBrand">
      <img src={Logo} alt="Taxi Moroder" />
      <span>Taxi <strong>Moroder</strong></span>
    </span>
  );

  return (
    <header className={`modernNav${scrolled ? ' modernNavScrolled' : ''}`}>
      <div className="navInner">
        {homeOnly ? <RouterLink to="/" className="navBrandLink"><Brand /></RouterLink> : (
          <ScrollLink to="Home" smooth duration={500} offset={-82} className="navBrandLink"><Brand /></ScrollLink>
        )}

        <nav className="navDesktop" aria-label={t('nav_primaryLabel')}>
          {(homeOnly ? pages.slice(0, 1) : pages).map((page) => homeOnly ? (
            <RouterLink key={page.target} to="/" className="navLink">{t(page.label)}</RouterLink>
          ) : (
            <ScrollLink key={page.target} to={page.target} smooth duration={500} offset={-88} spy activeClass="navLinkActive" className="navLink">
              {t(page.label)}
            </ScrollLink>
          ))}
        </nav>

        <div className="navControls">
          <div className="navLanguage">
            <button type="button" className="navLanguageButton" aria-expanded={languageOpen} onClick={() => setLanguageOpen((open) => !open)}>
              {currentLanguage}<span aria-hidden="true">⌄</span>
            </button>
            {languageOpen && (
              <div className="navLanguageMenu">
                {languages.map((language) => (
                  <button key={language.code} type="button" className={currentLanguage === language.code.toUpperCase() ? 'active' : ''} onClick={() => changeLanguage(language.code)}>
                    <span>{language.code.toUpperCase()}</span>{language.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a className="navCallButton" href={`tel:${PHONE_LINK}`}><LocalPhoneOutlinedIcon />{t('nav_call')}</a>
          <button type="button" className={`navMenuButton${menuOpen ? ' open' : ''}`} aria-label={t('nav_menu')} aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
            <span /><span />
          </button>
        </div>
      </div>

      <div className={`navMobilePanel${menuOpen ? ' open' : ''}`} aria-hidden={!menuOpen}>
        <nav aria-label={t('nav_primaryLabel')}>
          {(homeOnly ? pages.slice(0, 1) : pages).map((page, index) => homeOnly ? (
            <RouterLink key={page.target} to="/" onClick={closeMenu}><span>{String(index + 1).padStart(2, '0')}</span>{t(page.label)}</RouterLink>
          ) : (
            <ScrollLink key={page.target} to={page.target} smooth duration={500} offset={-145} onClick={closeMenu}>
              <span>{String(index + 1).padStart(2, '0')}</span>{t(page.label)}
            </ScrollLink>
          ))}
        </nav>
        <div className="navMobileActions">
          <a href={`tel:${PHONE_LINK}`}><LocalPhoneOutlinedIcon />{t('nav_call')}</a>
          <a href={`https://wa.me/${PHONE_LINK.replace('+', '')}`} target="_blank" rel="noopener noreferrer"><WhatsAppIcon />WhatsApp</a>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
